import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'

import { useQuantConfig } from '@/app/hooks/useQuantConfig'
import { useQuantFeed } from '@/app/hooks/useQuantFeed'
import { useQuantFeedCoverage } from '@/app/hooks/useQuantFeedCoverage'
import { useQuantRunFinishLogs } from '@/app/hooks/useQuantRunFinishLogs'
import { coverageRows, feedView } from '@/app/lib/quant/feedView'
import type { QuantShellContext } from '@/app/pages/QuantJupyter'
import { QuantFeedIndicator, type QuantFeedCoverage } from '@/ui/QuantFeedIndicator'
import { QuantHeader, type QuantHeaderTab } from '@/ui/QuantHeader'

const TABS: { label: string; href: string }[] = [
  { label: 'Strategies', href: '/strategies' },
  { label: 'Manual trade', href: '/manual' },
]

/**
 * Layout route for the Strategies and Manual screens: the Quant header (tabs, feed indicator)
 * around the routed content. The hue (`--h: 193`) is set once by QMC's `Shell`, which wraps this.
 */
export default function QuantShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const config = useQuantConfig()
  const feed = useQuantFeed()
  useQuantRunFinishLogs()

  // Spec 040: the feed indicator. Open is derived from the pathname, so a route change closes the panel.
  // `now` for time formatting: the last poll's receive time (refreshed every poll), else the mount time.
  const [mountedAt] = useState(() => Date.now())
  const now = feed.status === 'loaded' ? feed.receivedAt : mountedAt
  const view = feedView({ feed, now })
  const [openPath, setOpenPath] = useState<string | null>(null)
  const [openSeq, setOpenSeq] = useState(0)
  const feedOpen = openPath === location.pathname
  const cov = useQuantFeedCoverage(feedOpen, openSeq)
  const coverage: QuantFeedCoverage =
    cov.status === 'ready'
      ? {
          status: 'ready',
          rows: coverageRows({
            coverage: cov.data,
            feedState: feed.status === 'loaded' ? feed.state : null,
            feed: feed.status === 'loaded' ? feed.feed : null,
            now,
          }),
        }
      : cov.status === 'error'
        ? { status: 'error' }
        : { status: 'loading' }
  function onToggle() {
    if (feedOpen) {
      setOpenPath(null)
    } else {
      setOpenPath(location.pathname)
      setOpenSeq((n) => n + 1)
    }
  }

  // QMC has no Jupyter (spec 004 D6): the context keeps the field, always empty.
  const context: QuantShellContext = { jupyterUrl: '', feed }

  const tabs: QuantHeaderTab[] = TABS.map((tab) => ({
    ...tab,
    active: location.pathname.startsWith(tab.href),
  }))

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-ink text-paper">
      <QuantHeader
        tabs={tabs}
        kicker={`strategies read from the engine${config ? ` · ${config.max_concurrent_runs} run slots` : ''}`}
        onNavigate={(href) => navigate(href)}
        feed={
          <QuantFeedIndicator
            view={view}
            open={feedOpen}
            onToggle={onToggle}
            onClose={() => setOpenPath(null)}
            coverage={coverage}
          />
        }
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <Outlet context={context} />
      </div>
    </div>
  )
}
